import React from 'react'
import moment from 'moment'
import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'

import StatusMenu from '../../../../components/molecules/StatusMenu'
import TypesMenu from '../../../../components/molecules/TypesMenu'

import { formatDate } from '../../../../timeUtilities'
import { useAreYouSure } from '../../../../providers/AreYouSureProvider'
import { fetchTypes } from '../../../../slices/userType'
import { getReasonWarningText } from '../../../../utilities'
import { useCustomerContext } from '@SonarCX/providers/CustomerProvider'
import useUserPermissions from '../../../../hooks/permissions/useUserPermissions'
import useStyles from './styles'

const UserSummary = ({
  user,
  onStatusChange,
  onTypeChange,
  canEdit,
  isTeam
}) => {
  const dispatch = useAppDispatch()
  const { types, loaded, loading } = useAppSelector(
    storeState => storeState.userType
  )
  const { onFiles } = useCustomerContext()

  const { setAction, setReason, setSubtext, setTitle, setV2 } = useAreYouSure()
  const { checkCanSetInternal, checkCanSetExternal } = useUserPermissions()

  const classes = useStyles()

  React.useEffect(() => {
    if (!loaded && !loading) {
      dispatch(fetchTypes())
    }
  }, [loaded, loading, dispatch])

  const created = React.useMemo(() => {
    if (!user) {
      return
    }

    if (!user.created_at) {
      return formatDate(new Date().toISOString(), 'dd/MM/yyyy')
    } else {
      return formatDate(user.created_at, 'dd/MM/yyyy')
    }
  }, [user])

  const canSetInternal = React.useMemo(() => {
    // Arguments set to perform correct check
    return checkCanSetInternal(false, false)
  }, [checkCanSetInternal])

  const canSetExternal = React.useMemo(() => {
    // Arguments set to perform correct check
    return checkCanSetExternal(true, false, user)
  }, [checkCanSetExternal, user])

  const userTypeChange = React.useCallback(
    val => {
      if (user?.type_id === val) return

      const newType = types.find(type => type.id === val)
      const oldType = types.find(type => type.id === user?.type_id)

      if (newType?.external === oldType?.external) {
        onTypeChange(val)
        return
      }

      setTitle('ARE YOU SURE?')
      const reasonTxt = getReasonWarningText(newType, oldType)
      setReason(reasonTxt)
      setSubtext('')
      setAction(() => onTypeChange(val))
      setV2(true)
    },
    [
      onTypeChange,
      setTitle,
      setAction,
      setReason,
      setSubtext,
      setV2,
      types,
      user.type_id
    ]
  )

  if (onFiles) return null

  return (
    <section className={classes.root}>
      {isTeam ? (
        <>
          <div className={clsx(classes.row)}>
            <div className={classes.item}>
              <div className={classes.label}>Type</div>
              <TypesMenu
                id='user-type'
                selected={user?.type_id}
                onSelect={userTypeChange}
                className={classes.dropdown}
                disabled={!canEdit || (!canSetInternal && !canSetExternal)}
                hideInternal={!canSetInternal}
                hideExternal={!canSetExternal}
              />
            </div>
            <div className={classes.item}>
              <div className={classes.label}>Timezone</div>
              <div>{moment().format('h:mm a [EST]')}</div>
            </div>
          </div>
          <div className={clsx(classes.row, classes.team)}>
            <div className={classes.item}>
              <div className={classes.label}>Created</div>
              <div>{created}</div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={clsx(classes.row)}>
            <div className={classes.item}>
              <div className={classes.label}>Status</div>

              <StatusMenu
                id='user-status'
                selected={user?.status_id}
                onSelect={onStatusChange}
                className={classes.dropdown}
                disabled={!canEdit}
              />
            </div>

            <div className={classes.item}>
              <div className={classes.label}>Type</div>
              <TypesMenu
                id='user-type'
                selected={user?.type_id}
                onSelect={userTypeChange}
                className={classes.dropdown}
                disabled={!canEdit || (!canSetInternal && !canSetExternal)}
                hideInternal={!canSetInternal}
                hideExternal={!canSetExternal}
              />
            </div>
          </div>
          <div className={classes.row}>
            <div className={classes.item}>
              <div className={classes.label}>Timezone</div>
              <div>{moment().format('h:mm a [EST]')}</div>
            </div>
            <div className={classes.item}>
              <div className={classes.label}>Created</div>
              <div>{created}</div>
            </div>
          </div>
        </>
      )}
    </section>
  )
}

export default UserSummary
